import { DomainLandSituationType } from "@porchpass/api-client";
import type { LandFieldsFormData } from "@porchpass/ui";
import type {
  LandAlreadyOwnedFormData,
  LandCurrentlyFinancedFormData,
  LandLeasingFormData,
  LandPurchasingFormData,
} from "src/types/land";
import {
  fromLandAlreadyOwnedFormData,
  fromLandFinancedFormData,
  fromLandLeasingFormData,
  fromLandPurchasingFormData,
} from "src/forms/adapters/land";
import {
  greaterThanMinAmount,
  isPastOrCurrentDate,
  isSet,
  notEmpty,
  test,
  validate,
  type FormErrorShape,
  type ValidationMapperFunc,
} from "src/utils";

const alreadyOwnedValidationMapper: ValidationMapperFunc<
  LandAlreadyOwnedFormData
> = (data) => ({
  use_as_down_payment: [test(isSet, "Must make a selection")],
  land_value: [
    test(
      (val) => !data.use_as_down_payment || notEmpty(val),
      "Land value must not be empty",
    ),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  acreage: [
    test(
      (val) => !data.use_as_down_payment || notEmpty(val),
      "Total acreage must not be empty",
    ),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  purchase_month: [
    test(
      (val) => !data.use_as_down_payment || notEmpty(val),
      "Purchase month must not be empty",
    ),
  ],
  purchase_year: [
    test(
      (val) => !data.use_as_down_payment || notEmpty(val),
      "Purchase year must not be empty",
    ),
    test(
      () =>
        isPastOrCurrentDate(
          data.purchase_month ?? "",
          data.purchase_year ?? "",
        ),
      "Date must be in the past",
    ),
  ],
  has_frontage_public_road: [
    test(
      (val) => !data.use_as_down_payment || isSet(val),
      "Must make a selection",
    ),
  ],
  has_paved_road: [
    test(
      (val) => !data.use_as_down_payment || isSet(val),
      "Must make a selection",
    ),
  ],
  hoa_fee_amount: [
    test(greaterThanMinAmount(0, true), "Must be a positive number"),
  ],
});

const financedValidationMapper: ValidationMapperFunc<
  LandCurrentlyFinancedFormData
> = (data) => ({
  monthly_payment: [
    test(notEmpty, "Must provide a payment amount"),
    test(greaterThanMinAmount(0, true), "Amount must be greater than 0"),
  ],
  use_as_down_payment: [test(isSet, "Must make a selection")],
  is_refinance: [test(isSet, "Must make a selection")],
  purchase_price_payoff: [
    test(
      (val) =>
        (!data.use_as_down_payment && !data.is_refinance) || notEmpty(val),
      "Amount must not be empty",
    ),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  land_value: [
    test(
      (val) =>
        (!data.use_as_down_payment && !data.is_refinance) || notEmpty(val),
      "Must provide an estimated land value",
    ),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  acreage: [
    test(
      (val) =>
        (!data.use_as_down_payment && !data.is_refinance) || notEmpty(val),
      "Must provide a total acreage",
    ),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  purchase_month: [
    test(
      (val) =>
        (!data.use_as_down_payment && !data.is_refinance) || notEmpty(val),
      "Must include a month",
    ),
  ],
  purchase_year: [
    test(
      (val) =>
        (!data.use_as_down_payment && !data.is_refinance) || notEmpty(val),
      "Must include a year",
    ),
    test(
      () =>
        isPastOrCurrentDate(
          data.purchase_month ?? "",
          data.purchase_year ?? "",
        ),
      "Date must be in the past",
    ),
  ],
  land_owner: [
    test(
      (val) =>
        (!data.use_as_down_payment && !data.is_refinance) || notEmpty(val),
      "Must include a name",
    ),
  ],
  has_frontage_public_road: [
    test(
      (val) => (!data.use_as_down_payment && !data.is_refinance) || isSet(val),
      "Must make a selection",
    ),
  ],
  has_paved_road: [
    test(
      (val) => (!data.use_as_down_payment && !data.is_refinance) || isSet(val),
      "Must make a selection",
    ),
  ],
  hoa_fee_amount: [
    test(greaterThanMinAmount(0, true), "Must be a positive number"),
  ],
});

const purchasingValidationMapper: ValidationMapperFunc<
  LandPurchasingFormData
> = () => ({
  acreage: [
    test(notEmpty, "Must provide a total acreage"),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  purchase_price_payoff: [
    test(notEmpty, "Amount must not be empty"),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  land_owner: [test(notEmpty, "Must include a name")],
  has_frontage_public_road: [test(isSet, "Must make a selection")],
  has_paved_road: [test(isSet, "Must make a selection")],
  hoa_fee_amount: [
    test(greaterThanMinAmount(0, true), "Must be a positive number"),
  ],
});

const leasingValidationMapper: ValidationMapperFunc<
  LandLeasingFormData & { has_price_increase?: boolean }
> = (data) => ({
  lease_type: [test(isSet, "Must make a selection")],
  land_owner: [
    test((val) => !data.lease_type || notEmpty(val), "Must provide a name"),
  ],
  monthly_payment: [
    test(
      (val) => !data.lease_type || notEmpty(val),
      "Must provide a payment amount",
    ),
    test(greaterThanMinAmount(0), "Amount must be greater than 0"),
  ],
  price_increase_explanation: [
    test(
      (val) => (data.has_price_increase ? notEmpty(val) : true),
      "Must provide explanation",
    ),
  ],
  hoa_fee_amount: [
    test(greaterThanMinAmount(0, true), "Must be a positive number"),
  ],
});

export function validateLandFieldsFormData(value: LandFieldsFormData): {
  submitErrors: FormErrorShape<Record<string, unknown>>;
  fieldErrors: Record<string, string | undefined>;
} {
  if (!value.landSituation) {
    return {
      submitErrors: {
        landSituation: {
          message: "Must make a selection",
          blockSubmit: true,
        },
      },
      fieldErrors: { landSituation: "Must make a selection" },
    };
  }

  switch (value.landSituation) {
    case DomainLandSituationType.LandSituationTypeAlreadyOwned: {
      const data = fromLandAlreadyOwnedFormData(value);
      const submitErrors = validate(
        data,
        alreadyOwnedValidationMapper,
      ) as FormErrorShape<Record<string, unknown>>;
      return {
        submitErrors,
        fieldErrors: {
          useAsDownPayment: submitErrors.use_as_down_payment?.message,
          estimatedLandValue: submitErrors.land_value?.message,
          acreage: submitErrors.acreage?.message,
          purchaseDateMonth: submitErrors.purchase_month?.message,
          purchaseDateYear: submitErrors.purchase_year?.message,
          hasFrontagePublicRoad: submitErrors.has_frontage_public_road?.message,
          hasPavedRoad: submitErrors.has_paved_road?.message,
          hoaFeeAmount: submitErrors.hoa_fee_amount?.message,
        },
      };
    }
    case DomainLandSituationType.LandSituationTypeCurrentlyFinanced: {
      const data = fromLandFinancedFormData(value);
      const submitErrors = validate(
        data,
        financedValidationMapper,
      ) as FormErrorShape<Record<string, unknown>>;
      return {
        submitErrors,
        fieldErrors: {
          monthlyLandPayment: submitErrors.monthly_payment?.message,
          useAsDownPayment: submitErrors.use_as_down_payment?.message,
          isRefinance: submitErrors.is_refinance?.message,
          existingMortgageBalance: submitErrors.purchase_price_payoff?.message,
          estimatedLandValue: submitErrors.land_value?.message,
          acreage: submitErrors.acreage?.message,
          purchaseDateMonth: submitErrors.purchase_month?.message,
          purchaseDateYear: submitErrors.purchase_year?.message,
          landlordName: submitErrors.land_owner?.message,
          hasFrontagePublicRoad: submitErrors.has_frontage_public_road?.message,
          hasPavedRoad: submitErrors.has_paved_road?.message,
          hoaFeeAmount: submitErrors.hoa_fee_amount?.message,
        },
      };
    }
    case DomainLandSituationType.LandSituationTypePurchasing: {
      const data = fromLandPurchasingFormData(value);
      const submitErrors = validate(
        data,
        purchasingValidationMapper,
      ) as FormErrorShape<Record<string, unknown>>;
      return {
        submitErrors,
        fieldErrors: {
          existingMortgageBalance: submitErrors.purchase_price_payoff?.message,
          acreage: submitErrors.acreage?.message,
          landlordName: submitErrors.land_owner?.message,
          hasFrontagePublicRoad: submitErrors.has_frontage_public_road?.message,
          hasPavedRoad: submitErrors.has_paved_road?.message,
          hoaFeeAmount: submitErrors.hoa_fee_amount?.message,
        },
      };
    }
    case DomainLandSituationType.LandSituationTypeLeasing: {
      const data = fromLandLeasingFormData(value);
      const submitErrors = validate(
        data,
        leasingValidationMapper,
      ) as FormErrorShape<Record<string, unknown>>;
      return {
        submitErrors,
        fieldErrors: {
          leaseType: submitErrors.lease_type?.message,
          landlordName: submitErrors.land_owner?.message,
          monthlyLeasePayment: submitErrors.monthly_payment?.message,
          priceIncreaseExplanation:
            submitErrors.price_increase_explanation?.message,
          hoaFeeAmount: submitErrors.hoa_fee_amount?.message,
        },
      };
    }
    case DomainLandSituationType.LandSituationTypeFamily:
    case DomainLandSituationType.LandSituationReservation:
      return { submitErrors: {}, fieldErrors: {} };
    default:
      return {
        submitErrors: {
          landSituation: {
            message: "Must make a selection",
            blockSubmit: true,
          },
        },
        fieldErrors: { landSituation: "Must make a selection" },
      };
  }
}
